import React, { Fragment, useState, useEffect } from "react";
import styles from "./SubscriptionItems.module.scss";

import PlaceholderImage from "assets/img/placeholder.png";
import Button from "shared/Button";
import ProductSelectorModal from "./modals/ProductSelectorModal";

import { useSelector, useDispatch } from "react-redux";
import RemoveProductModal from "./modals/RemoveProductModal";
import { NavLink } from "react-router-dom";
import SparklesBadge from "./SparklesBadge";
import Modal from "shared/Modal";
import { fetchPlanConfig } from "app/config/actions";
import { priceFormatter } from "./utility";
import _ from "lodash";
import cx from "classnames";

const SubscriptionItems = ({
    subscriptionId,
    products,
    countryCode,
    currency,
    gift,
    setProducts,
    allowEdit,
    isWhite = false,
    hasPadding = true,
    onRemoveOneTimeProduct,
}) => {
    const dispatch = useDispatch();
    const variantIdMap = useSelector((s) =>
        _.get(s, `config.planConfigs.${countryCode}.variantIdMap`),
    );
    const displayedProducts = products || [];
    const editableProducts = displayedProducts.filter(
        (product) => product.type !== "onetime",
    );

    useEffect(() => {
        if (!variantIdMap) {
            dispatch(
                fetchPlanConfig({
                    country_code: countryCode,
                    currency: currency,
                }),
            );
        }
    }, [variantIdMap, countryCode, currency, dispatch]);

    function handleQuantityUpdate(action, idx) {
        const itemPrice =
            editableProducts[idx].price.amount /
            editableProducts[idx].quantity;
        const newQuantity =
            editableProducts[idx].quantity + (action === "add" ? 1 : -1);
        const newTotalPrice = newQuantity * itemPrice;
        const newProducts = editableProducts.map((product, productIdx) =>
            productIdx !== idx
                ? product
                : {
                      ...product,
                      quantity: newQuantity,
                      price: {
                          amount: `${newTotalPrice}`,
                          currency: currency,
                          formatted: priceFormatter({
                              price: newTotalPrice,
                              countryCode: countryCode,
                              currency: currency,
                          }),
                      },
                      total: {
                          amount: `${newTotalPrice}`,
                          currency: currency,
                          formatted: priceFormatter({
                              price: newTotalPrice,
                              countryCode: countryCode,
                              currency: currency,
                          }),
                      },
                  },
        );
        setProducts(newProducts);
    }

    function removeItemFromSubscription(idx) {
        setProducts(
            editableProducts.filter((_, productIdx) => productIdx !== idx),
        );
    }

    function handleProductSwap(payload, idx) {
        // only update if it's not the same product variant
        if (payload.variant_id !== editableProducts[idx].variant.id) {
            const variant = variantIdMap[payload.variant_id];
            const newProducts = [...editableProducts];

            // if the product is the same and only the variant is updated, only update the variant field
            if (payload.product_id === editableProducts[idx].product.id) {
                newProducts.splice(idx, 1, {
                    ...editableProducts[idx],
                    variant: {
                        id: payload.variant_id,
                        title: variant.item.title,
                        options: [variant.item.title],
                    },
                });
            } else {
                // otherwise update everything apart from quantity and remove id
                const newPrice =
                    editableProducts[idx].quantity * variant.item.price;
                newProducts.splice(idx, 1, {
                    quantity: editableProducts[idx].quantity,
                    product: {
                        id: payload.product_id,
                        title: variant.format.name,
                    },
                    variant: {
                        id: payload.variant_id,
                        title: variant.item.title,
                        options: [variant.item.title],
                    },
                    price: {
                        amount: `${newPrice}`,
                        currency: currency,
                        formatted: priceFormatter({
                            price: newPrice,
                            countryCode: countryCode,
                            currency: currency,
                        }),
                    },
                    total: {
                        amount: `${newPrice}`,
                        currency: currency,
                        formatted: priceFormatter({
                            price: newPrice,
                            countryCode: countryCode,
                            currency: currency,
                        }),
                    },
                });
            }
            setProducts(newProducts);
        }
    }

    const ProductsList = () => (
        <div
            className={`rounded ${hasPadding ? "p-3 border" : ""} ${"bg-white"}`}
        >
            {displayedProducts.map((product, idx) => {
                const isOneTimeProduct = product.type === "onetime";
                const editableProductIdx = editableProducts.indexOf(product);

                return (
                    <SubscriptionProduct
                        {...product}
                        key={`${product.type || "subscription"}-${
                            product.id || product.variant?.id
                        }-${idx}`}
                        idx={editableProductIdx}
                        isLast={displayedProducts.length === idx + 1}
                        handleQuantityUpdate={
                            !isOneTimeProduct &&
                            setProducts &&
                            handleQuantityUpdate
                        }
                        handleProductSwap={
                            isOneTimeProduct
                                ? undefined
                                : handleProductSwap
                        }
                        removeItemFromSubscription={
                            isOneTimeProduct
                                ? undefined
                                : removeItemFromSubscription
                        }
                        gift={gift}
                        items={editableProducts}
                        countryCode={countryCode}
                        variantIdMap={variantIdMap}
                        onRemoveOneTimeProduct={onRemoveOneTimeProduct}
                        detailsTo={
                            !allowEdit
                                ? `/subscriptions/${subscriptionId}/details`
                                : null
                        }
                    />
                );
            })}
        </div>
    );
    return <ProductsList />;
};

const SubscriptionProduct = ({
    idx,
    price,
    total,
    product,
    gift,
    countryCode,
    quantity,
    variant,
    isLast,
    items,
    variantIdMap,
    handleQuantityUpdate,
    handleProductSwap,
    removeItemFromSubscription,
    id,
    type,
    onRemoveOneTimeProduct,
    detailsTo,
}) => {
    const [modal, setModal] = useState(false);
    const blendVariants = [...new Set(variant.options)];
    const variantText =
        blendVariants.length === 1
            ? blendVariants[0]
            : variant.options.join(" / ");
    const showVariantText = variantText !== "Default Title";

    const productContent = (
        <>
            <div
                className={`position-relative d-none d-sm-block me-3 ${styles.imageContainer}`}
            >
                <img
                    src={product.image ? product.image.url : PlaceholderImage}
                    className="rounded"
                    alt={product.title}
                />
                {!handleQuantityUpdate && <Quantity quantity={quantity} />}
            </div>
            <div className="d-flex flex-column flex-md-row flex-grow-1">
                <div className="d-flex flex-column flex-grow-1">
                    <div className="d-flex flex-column flex-md-row">
                        <p
                            className={`gkit-p-20 gkit-sm-p-17 fw-bold mt-2 mt-md-0 mb-0 ${
                                gift
                                    ? "order-2 order-md-1 mt-2 mt-md-0 flex-grow-1"
                                    : ""
                            }`}
                        >
                            {!handleQuantityUpdate && (
                                <span className="d-inline d-sm-none">
                                    {quantity}x{" "}
                                </span>
                            )}
                            {product.title}
                            {type === "onetime" && (
                                <span className={styles.oneTimeBadge}>
                                    One-time
                                </span>
                            )}
                        </p>
                        {gift && (
                            <SparklesBadge
                                text="Gifted"
                                className="order-1 order-md-2"
                            />
                        )}
                    </div>
                    {showVariantText && (
                        <p className="gkit-p-15 mt-0 mb-1">{variantText}</p>
                    )}
                    {!gift && (
                        <p className="gkit-p-15 gkit-sm-p-13 fw-bold mt-0 mb-0">
                            {total.amount !== price.amount ? (
                                <span className="d-flex">
                                    <span className="text-success me-2">
                                        {total.formatted}
                                    </span>
                                    <span className="gkit-p-15 gkit-sm-p-13">
                                        <s>{price.formatted}</s>
                                    </span>
                                </span>
                            ) : (
                                price.formatted
                            )}
                        </p>
                    )}
                </div>
                {handleQuantityUpdate && (
                    <div className={styles.buttonWraper}>
                        {variant && variantIdMap && handleProductSwap && (
                            <Fragment>
                                <Button
                                    btnType="secondary"
                                    className={gift ? "ms-md-3" : "ms-3"}
                                    onClick={() => setModal("productSelector")}
                                >
                                    Swap
                                </Button>
                                {variant && variantIdMap && (
                                    <ProductSelectorModal
                                        initialVariantId={
                                            variantIdMap[variant.id]
                                                ? variant.id
                                                : undefined
                                        }
                                        isOpen={modal === "productSelector"}
                                        handleClose={() => setModal(null)}
                                        handleAction={(payload) => {
                                            handleProductSwap(payload, idx);
                                            setModal(null);
                                        }}
                                        gift={gift}
                                        countryCode={countryCode}
                                    />
                                )}
                            </Fragment>
                        )}
                        {items.length > 1 && (
                            <Fragment>
                                <p
                                    className={cx(
                                        styles.removeBtn,
                                        gift ? "ms-md-3" : "ms-3",
                                    )}
                                    onClick={() => setModal("delete")}
                                >
                                    Remove
                                </p>
                            </Fragment>
                        )}
                        <RemoveProductModal
                            isOpen={modal === "delete"}
                            item="Subscription Item"
                            handleClose={() => setModal(null)}
                            handleAction={() => removeItemFromSubscription(idx)}
                        />
                        <Modal
                            isOpen={modal === "last"}
                            title="Your plan can’t be empty"
                            handleClose={() => setModal(null)}
                            hideCloseButton={false}
                        />
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div className={`d-flex ${!isLast ? "mb-4" : ""}`}>
            {detailsTo ? (
                <NavLink className={styles.productLink} to={detailsTo}>
                    {productContent}
                </NavLink>
            ) : (
                productContent
            )}
            {!handleQuantityUpdate &&
                type === "onetime" &&
                onRemoveOneTimeProduct && (
                    <div className={styles.buttonWraper}>
                        <button
                            type="button"
                            className={cx(
                                styles.removeBtn,
                                styles.removeButton,
                            )}
                            aria-label={`Remove ${product.title} from your next order`}
                            onClick={() => setModal("deleteOneTimeProduct")}
                        >
                            Remove
                        </button>
                        <RemoveProductModal
                            isOpen={modal === "deleteOneTimeProduct"}
                            item={product.title}
                            handleClose={() => setModal(null)}
                            handleAction={() =>
                                onRemoveOneTimeProduct(id)
                            }
                        />
                    </div>
                )}
        </div>
    );
};

export const Quantity = ({ quantity }) => (
    <div className={`gkit-bg-red rounded-circle ${styles.quantity}`}>
        <p className="gkit-p-15 text-white fw-bold">{quantity}</p>
    </div>
);

export default SubscriptionItems;
